import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);

      const response = await fetch('https://readdy.ai/api/form/d5n2as944v703eh9198g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '' });
        setTimeout(() => { setFormStatus('idle'); setMenuOpen(false); }, 2000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 3000);
      }
    } catch {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const formElement = e.currentTarget;
      const emailInput = formElement.querySelector('input[name="email"]') as HTMLInputElement;
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('email', emailInput.value);

      const response = await fetch('https://readdy.ai/api/form/d5n2as944v703eh91990', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        emailInput.value = '';
        alert('Thank you for subscribing!');
      } else {
        alert('Something went wrong. Please try again.');
      }
    } catch {
      alert('Something went wrong. Please try again.');
    }
  };

  useEffect(() => {
    const handleScroll = () => { setScrolled(window.scrollY > 50); };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      <SEOHead
        title="Technelle | Wisconsin AI Marketing & Consulting Agency | Waukesha"
        description="Transform your Wisconsin business with AI-powered marketing strategies and workflow automation. Based in Waukesha, serving all of Wisconsin. 20+ years of expertise helping SMBs achieve measurable revenue growth through intelligent marketing systems."
        keywords="AI marketing Waukesha, marketing agency Wisconsin, workflow automation, AI consulting services, demand generation, lead generation Wisconsin, marketing automation, Waukesha business consulting, Wisconsin AI implementation"
        canonical="https://technelle.ai/"
        ogTitle="Technelle | Wisconsin AI Marketing & Consulting Agency | Waukesha"
        ogDescription="20+ years of marketing expertise combined with cutting-edge AI. Drive measurable growth for your Wisconsin business with intelligent marketing systems that adapt in real-time."
        ogImage="https://static.readdy.ai/image/cb419a93222bfd6610832c3481bbaa16/2c1578fc61a5e3d5ea92991827e265f3.png"
        ogUrl="https://technelle.ai/"
      />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A1628]">
        <div className="container mx-auto px-4 sm:px-8 h-36 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/d2c67bee-2653-4219-a221-2e9d779e273e_ChatGPT-Image-Apr-2-2026-02_21_57-PM.png?v=d5a360501a5e3b86884e2dc0f7061a82" alt="Technelle - AI Marketing & Consulting" className="h-36" />
          </div>
          <div className="hidden lg:flex items-center gap-10">
            <Link to="/" className="text-[15px] text-white/80 hover:text-white transition-colors font-medium">Home</Link>
            <Link to="/about" className="text-[15px] text-white/80 hover:text-white transition-colors font-medium">About</Link>
            <Link to="/services" className="text-[15px] text-white/80 hover:text-white transition-colors font-medium">Services</Link>
            <Link to="/industries" className="text-[15px] text-white/80 hover:text-white transition-colors font-medium">Industries</Link>
            <Link to="/resources" className="text-[15px] text-white/80 hover:text-white transition-colors font-medium">Resources</Link>
            <Link to="/how-we-work" className="text-[15px] text-white/80 hover:text-white transition-colors font-medium">How We Work</Link>
            <Link to="/service-areas" className="text-[15px] text-white/80 hover:text-white transition-colors font-medium">Service Areas</Link>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              className="hidden lg:block bg-[#2196F3] text-white px-6 sm:px-8 py-3 rounded-md text-[15px] font-medium hover:bg-[#1976D2] transition-colors whitespace-nowrap cursor-pointer min-w-[44px] min-h-[44px]"
              aria-label="Contact us today"
            >
              Contact Us Today
            </Link>
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileNavOpen(true)}
              className="lg:hidden w-11 h-11 flex items-center justify-center text-white cursor-pointer"
              aria-label="Open mobile menu"
            >
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>
        {/* Electric blue separator line */}
        <div className="h-[2px] bg-[#2196F3]"></div>
      </nav>

      {/* Mobile Nav Drawer */}
      {mobileNavOpen && (
        <div className="fixed inset-0 bg-black/60 z-[200] lg:hidden" onClick={() => setMobileNavOpen(false)}>
          <div className="absolute top-0 right-0 w-72 h-full bg-[#0A1628] shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 h-20 border-b border-white/10">
              <span className="text-white font-semibold text-[16px]">Menu</span>
              <button onClick={() => setMobileNavOpen(false)} className="w-10 h-10 flex items-center justify-center text-white cursor-pointer" aria-label="Close menu">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <nav className="flex flex-col px-6 py-8 gap-6">
              <Link to="/" onClick={() => setMobileNavOpen(false)} className="text-white/80 hover:text-white text-[16px] font-medium transition-colors">Home</Link>
              <Link to="/about" onClick={() => setMobileNavOpen(false)} className="text-white/80 hover:text-white text-[16px] font-medium transition-colors">About</Link>
              <Link to="/services" onClick={() => setMobileNavOpen(false)} className="text-white/80 hover:text-white text-[16px] font-medium transition-colors">Services</Link>
              <Link to="/industries" onClick={() => setMobileNavOpen(false)} className="text-white/80 hover:text-white text-[16px] font-medium transition-colors">Industries</Link>
              <Link to="/resources" onClick={() => setMobileNavOpen(false)} className="text-white/80 hover:text-white text-[16px] font-medium transition-colors">Resources</Link>
              <Link to="/how-we-work" onClick={() => setMobileNavOpen(false)} className="text-white/80 hover:text-white text-[16px] font-medium transition-colors">How We Work</Link>
              <Link to="/service-areas" onClick={() => setMobileNavOpen(false)} className="text-white/80 hover:text-white text-[16px] font-medium transition-colors">Service Areas</Link>
              <Link to="/case-studies" onClick={() => setMobileNavOpen(false)} className="text-white/80 hover:text-white text-[16px] font-medium transition-colors">Case Studies</Link>
              <Link to="/faq" onClick={() => setMobileNavOpen(false)} className="text-white/80 hover:text-white text-[16px] font-medium transition-colors">FAQ</Link>
            </nav>
            <div className="px-6 mt-auto pb-8">
              <button
                onClick={() => { setMobileNavOpen(false); setMenuOpen(true); }}
                className="w-full bg-[#2196F3] text-white py-4 rounded-md text-[15px] font-semibold hover:bg-[#1976D2] transition-colors cursor-pointer"
              >
                Get Free Audit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Overlay */}
      {menuOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-start justify-end">
          <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl">
            <div className="p-8 sm:p-12">
              <div className="flex items-center justify-between mb-12">
                <h2 className="text-[28px] sm:text-[32px] font-bold text-[#1a1a1a]">Get Your Free Marketing Audit</h2>
                <button
                  onClick={() => setMenuOpen(false)}
                  className="text-[#1a1a1a] hover:text-[#2196F3] transition-colors cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Close contact form"
                >
                  <span className="text-[15px] font-medium">Close</span>
                </button>
              </div>
              <div className="mb-8">
                <p className="text-[16px] text-gray-700 leading-relaxed mb-4">In just 30 minutes, we'll show you:</p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-[#2196F3] text-lg mt-0.5" aria-hidden="true"></i>
                    <span className="text-[15px] text-gray-700">3 quick wins you can implement this week</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-[#2196F3] text-lg mt-0.5" aria-hidden="true"></i>
                    <span className="text-[15px] text-gray-700">Where your competitors are beating you (and how to fix it)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-[#2196F3] text-lg mt-0.5" aria-hidden="true"></i>
                    <span className="text-[15px] text-gray-700">AI opportunities that could 2x your leads in 90 days</span>
                  </li>
                </ul>
                <div className="bg-blue-50 border-l-4 border-[#2196F3] p-4 mb-6">
                  <p className="text-[14px] text-gray-700"><strong className="text-[#2196F3]">Worth $500</strong> — Yours free. No pitch, no obligation.</p>
                </div>
              </div>
              <form className="space-y-6" onSubmit={handleFormSubmit} data-readdy-form id="contact-form">
                <div>
                  <input
                    type="text" name="name" value={formData.name} onChange={handleInputChange}
                    placeholder="Your Name *" required aria-label="Your Name"
                    className="w-full border-b border-gray-300 py-3 text-[15px] focus:outline-none focus:border-[#2196F3] transition-colors"
                  />
                </div>
                <div>
                  <input
                    type="email" name="email" value={formData.email} onChange={handleInputChange}
                    placeholder="Email Address *" required aria-label="Email Address"
                    className="w-full border-b border-gray-300 py-3 text-[15px] focus:outline-none focus:border-[#2196F3] transition-colors"
                  />
                </div>
                <button
                  type="submit" disabled={formStatus === 'submitting'}
                  className="w-full bg-[#2196F3] text-white py-4 rounded-md text-[15px] font-medium hover:bg-[#1976D2] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                  aria-label="Submit contact form"
                >
                  {formStatus === 'submitting' ? 'Submitting...' : formStatus === 'success' ? 'Sent Successfully!' : 'Claim My Free Audit Now'}
                </button>
                {formStatus === 'success' && <div className="text-green-600 text-[14px] text-center" role="status">Thank you! We'll contact you within 24 hours to schedule your session.</div>}
                {formStatus === 'error' && <div className="text-red-600 text-[14px] text-center" role="status">Something went wrong. Please try again.</div>}
              </form>
              <div className="mt-12 pt-8 border-t border-gray-200">
                <h3 className="text-[18px] font-semibold text-[#1a1a1a] mb-6">Prefer to reach out directly?</h3>
                <div className="space-y-4">
                  <div>
                    <div className="text-[14px] text-gray-600 mb-1">Email</div>
                    <a href="mailto:melissa@technelle.ai" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors">melissa@technelle.ai</a>
                  </div>
                  <div>
                    <div className="text-[14px] text-gray-600 mb-1">Phone</div>
                    <a href="tel:4145309114" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors">414-530-9114</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative flex items-center justify-center overflow-hidden bg-[#0A1628]">
        <div className="absolute inset-0 bg-[#0A1628]"></div>
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-40 pb-20 w-full">
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="text-[38px] sm:text-[56px] lg:text-[76px] leading-[1.1] font-bold text-white mb-6 font-serif">
              AI-Driven Marketing That<br />
              <span className="relative inline-block">
                Drives Measurable Growth
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 500 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M2 8C125 2 375 2 498 8" stroke="#2196F3" strokeWidth="3" strokeLinecap="round"/>
                </svg>
              </span>
            </h1>
            <p className="text-[16px] sm:text-[20px] lg:text-[22px] text-white/95 max-w-3xl mx-auto mb-4 leading-relaxed">
              A 20-year marketing operator now integrating AI into proven strategy — helping Wisconsin businesses grow without the guesswork.
            </p>
            <p className="text-[14px] sm:text-[16px] text-white/70 max-w-3xl mx-auto mb-10 leading-relaxed">
              Specializing in Wisconsin businesses with 5 to 150 employees and up to $30M in annual revenue — big enough to have real growth goals, focused enough to move fast.
            </p>
            <div className="flex items-center justify-center gap-4 flex-wrap mb-6">
              <button
                onClick={() => setMenuOpen(true)}
                className="bg-[#2196F3] text-white px-6 sm:px-12 py-4 sm:py-5 rounded-md text-[15px] sm:text-[17px] font-semibold hover:bg-[#1976D2] transition-all shadow-2xl cursor-pointer whitespace-nowrap min-w-[44px] min-h-[44px]"
                aria-label="See where your competitors are beating you"
              >
                See Where Your Competitors Are Beating You
              </button>
            </div>
            {/* Stats badges — below CTA */}
            <div className="flex items-center justify-center gap-3 sm:gap-4 flex-wrap mb-6">
              {[
                { label: '20+ Years Experience' },
                { label: '1,000+ Projects Delivered' },
                { label: '40,000+ Hours Expertise' },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="flex items-center gap-2 border border-[#2196F3] rounded-full px-4 py-2"
                >
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-[#2196F3] text-sm" aria-hidden="true"></i>
                  </span>
                  <span className="text-white text-[13px] sm:text-[14px] font-medium whitespace-nowrap">{stat.label}</span>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* This Is Who I Am Section */}
      <section className="bg-[#0A1628] py-24 border-t border-white/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">

            {/* Eyebrow */}
            <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-5">
              This is who I am
            </div>

            {/* Headline */}
            <h2 className="text-[36px] sm:text-[48px] lg:text-[54px] font-serif font-bold text-white leading-tight mb-8">
              I Do Not Replace Your People.<br />I Make Them Extraordinary.
            </h2>

            {/* Body */}
            <p className="text-[17px] sm:text-[18px] text-white/80 leading-relaxed mb-10">
              I am Melissa, founder of Technelle and a 20-year marketing operator based in Waukesha, Wisconsin. I have spent two decades working hands-on across trades and contractors, industrial manufacturing, renewable energy, e-commerce, food and beverage, and hospitality. I use AI every single day — not as a gimmick, but as a force multiplier that makes businesses and their people dramatically more capable. I recently helped an $80,000-a-year employee compress a full month of work into one hour. She did not get replaced. She became the most valuable person on the team. That is the only outcome I am interested in.
            </p>

            {/* Pullout Quote */}
            <blockquote className="border-l-4 border-[#2196F3] pl-6 sm:pl-8 mb-12">
              <p className="text-[20px] sm:text-[24px] font-semibold text-white leading-snug">
                Most AI consultants understand the tools but have never run a business. Most marketing consultants understand business but do not use AI. I do both — at 20 years deep.
              </p>
            </blockquote>

            {/* Credential Badges */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
              {[
                { label: '20 Years Experience' },
                { label: '1,000+ Projects Delivered' },
                { label: '8+ Industries' },
                { label: 'AI-Powered Daily' },
              ].map((badge) => (
                <div
                  key={badge.label}
                  className="border border-[#2196F3] rounded-lg px-4 py-4 text-center"
                >
                  <span className="text-white text-[14px] sm:text-[15px] font-semibold leading-snug">{badge.label}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <Link
              to="/services"
              className="inline-flex items-center gap-2 bg-[#2196F3] text-white px-8 py-4 rounded-md text-[15px] font-semibold hover:bg-[#1976D2] transition-colors cursor-pointer whitespace-nowrap"
              aria-label="See where Melissa can help you"
            >
              See Where I Can Help You
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>

          </div>
        </div>
      </section>

      {/* Personal Credibility Section */}
      <section className="bg-[#0A1628] py-20 border-t border-white/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

            {/* Left: Headshot */}
            <div className="flex justify-center lg:justify-start">
              <div className="relative">
                {/* Electric blue border frame */}
                <div className="absolute -inset-[3px] rounded-lg bg-[#2196F3]"></div>
                <div className="relative w-full max-w-[420px] h-[500px] rounded-lg overflow-hidden">
                  <img
                    src="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/055ed85d-fdf9-4a25-aaed-04055b9c4167_Headshot.png?v=34f4a642e8cdde208d06262d9ecec027"
                    alt="Melissa Thomas - Founder of Technelle, Wisconsin AI Marketing Expert based in Waukesha"
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                    decoding="async"
                    width="420"
                    height="500"
                  />
                </div>
                {/* Floating badge */}
                <div className="absolute -bottom-4 -right-4 bg-[#2196F3] text-white px-4 py-3 rounded-lg">
                  <div className="text-[22px] font-bold leading-none">20+</div>
                  <div className="text-[11px] uppercase tracking-wider font-medium mt-0.5">Years Experience</div>
                </div>
              </div>
            </div>

            {/* Right: Content */}
            <div>
              {/* Eyebrow */}
              <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-4">
                Your Wisconsin AI Marketing Expert
              </div>

              {/* Headline */}
              <h2 className="text-[36px] sm:text-[42px] font-serif font-bold text-white leading-tight mb-6">
                20 Years of Real Results.<br />Not Agency Theater.
              </h2>

              {/* Body */}
              <p className="text-[16px] text-white/80 leading-relaxed mb-8">
                I'm Melissa Thomas, founder of Technelle and a 20-year marketing operator based in Waukesha, Wisconsin. Over two decades I have worked hands-on across trades and contractors, industrial manufacturing, renewable energy, e-commerce, food and beverage, and more — giving me a rare depth of real-world experience that most agencies simply cannot match.
              </p>
              <p className="text-[16px] text-white/80 leading-relaxed mb-8">
                I've personally delivered results for HVAC companies, restaurants, manufacturers, and online brands — including building TeenWorkHub from zero to a national waitlist with no paid marketing budget. When you work with Technelle, you work directly with me. No junior account managers. No outsourced execution. No national agency playbook applied to your local market. Just senior strategy and hands-on results from someone who has been in the trenches across your industry.
              </p>

              {/* Bullet Points */}
              <ul className="space-y-3 mb-10">
                {[
                  'Trades & contractors — HVAC, plumbing, electrical, and home services',
                  'Industrial manufacturing & renewable energy',
                  'E-commerce, food & beverage, and hospitality',
                  '1,000+ projects delivered across 20+ years in Wisconsin and beyond',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-[#2196F3] text-lg" aria-hidden="true"></i>
                    </div>
                    <span className="text-[15px] text-white/85 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Link
                to="/services"
                className="inline-flex items-center gap-2 border-2 border-[#2196F3] text-[#2196F3] hover:bg-[#2196F3] hover:text-white px-8 py-4 rounded-md text-[15px] font-semibold transition-all cursor-pointer whitespace-nowrap"
                aria-label="See Melissa's work and services"
              >
                See My Work
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>

          </div>
        </div>
      </section>

      {/* AI Proof Point Section */}
      <section className="bg-[#0d1f35] py-20 border-t border-white/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">

            {/* Pullout Quote */}
            <blockquote className="border-l-4 border-[#2196F3] pl-8 sm:pl-10 mb-14">
              <p className="text-[20px] sm:text-[26px] lg:text-[28px] font-semibold text-white leading-snug">
                "An $80,000-a-year employee just compressed a full month of work into one hour. She did not get replaced. She became the most valuable person on the team. That is what AI does when it is implemented correctly — and it is what Technelle does for Wisconsin businesses every day."
              </p>
            </blockquote>

            {/* Stat Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
              {[
                { stat: '$6,000+', label: 'In labor cost recovered in one engagement' },
                { stat: '1 hour', label: 'vs. one full month of manual work' },
                { stat: '0', label: 'Staff members replaced' },
              ].map((card) => (
                <div key={card.label} className="border border-[#2196F3]/40 rounded-xl px-6 py-7 bg-[#0A1628] text-center">
                  <div className="text-[40px] sm:text-[44px] font-bold text-[#2196F3] leading-none mb-3">{card.stat}</div>
                  <div className="text-[14px] text-white/65 leading-snug">{card.label}</div>
                </div>
              ))}
            </div>

            {/* Body text */}
            <p className="text-[17px] sm:text-[18px] text-white/75 leading-relaxed mb-6">
              This is not a hypothetical. This happened in a real Wisconsin business. The tools exist today. The only variable is whether you have someone who knows how to use them for your specific operation.
            </p>

            {/* Text link */}
            <Link
              to="/ai-staff-productivity-wisconsin"
              className="inline-flex items-center gap-2 text-[#2196F3] text-[15px] font-semibold hover:underline cursor-pointer"
              aria-label="Read the full AI staff productivity case study"
            >
              Read the full case study
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>

          </div>
        </div>
      </section>

      {/* Real Client Websites Portfolio */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-8">
          <div className="text-center mb-16">
            <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-4">Real Work. Real Results.</div>
            <h2 className="text-[42px] font-serif font-bold text-[#1a1a1a] mb-4">
              Live Websites We've{' '}
              <span className="relative inline-block text-[#2196F3]">
                Launched
                <svg className="absolute -bottom-1 left-0 w-full" viewBox="0 0 160 6" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M2 4C50 2 110 2 148 4" stroke="#2196F3" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </span>
            </h2>
            <p className="text-[18px] text-gray-600 max-w-2xl mx-auto leading-relaxed">
              From local Wisconsin service businesses to nationwide platforms — here's a look at real sites built and launched through Technelle's expertise.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Teen Work Hub */}
            <div className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-[#2196F3]/30 transition-all hover:-translate-y-1">
              <a href="https://www.teenworkhub.com" target="_blank" rel="noopener noreferrer" className="block">
                <div className="relative h-[220px] overflow-hidden">
                  <img
                    src="https://public.readdy.ai/ai/img_res/52dda620210c6b5d0504c806895136c8.jpg"
                    alt="Teen Work Hub - teen job marketplace website built by Technelle"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                    width="600"
                    height="440"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                  <div className="absolute top-4 left-4">
                    <span className="bg-white/90 text-[#1a1a1a] text-[11px] uppercase tracking-wider font-bold px-3 py-1 rounded-full">Platform / Marketplace</span>
                  </div>
                  <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-10 h-10 bg-[#2196F3] rounded-full flex items-center justify-center">
                      <i className="ri-external-link-line text-white text-lg" aria-hidden="true"></i>
                    </div>
                  </div>
                </div>
              </a>
              <div className="p-7">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-[20px] font-bold text-[#1a1a1a] leading-tight">Teen Work Hub</h3>
                    <a href="https://www.teenworkhub.com" target="_blank" rel="noopener noreferrer" className="text-[13px] text-[#2196F3] hover:underline font-medium">teenworkhub.com</a>
                  </div>
                  <a href="https://www.teenworkhub.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-[#2196F3] hover:text-white transition-colors text-[#1a1a1a]" aria-label="Visit Teen Work Hub website">
                    <i className="ri-arrow-right-up-line text-base" aria-hidden="true"></i>
                  </a>
                </div>
                <p className="text-[14px] text-gray-600 leading-relaxed mb-5">
                  A nationwide job marketplace connecting teens ages 14–19 with local businesses. Full platform with dual sign-up flows for teens and employers, job listings, and safe verification systems.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="text-[12px] bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium">Web Platform</span>
                  <span className="text-[12px] bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium">Marketplace</span>
                  <span className="text-[12px] bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium">National Launch</span>
                </div>
              </div>
            </div>

            {/* Tipsy Tee Tavern */}
            <div className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-[#2196F3]/30 transition-all hover:-translate-y-1">
              <a href="https://tipsyteetavern.com/" target="_blank" rel="noopener noreferrer" className="block">
                <div className="relative h-[220px] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Golf%20themed%20bar%20and%20tavern%20restaurant%20website%20mockup%20with%20warm%20wood%20tones%20and%20green%20golf%20course%20accents%20showing%20a%20lively%20sports%20bar%20atmosphere%20with%20drinks%20menu%20and%20event%20calendar%20on%20a%20modern%20hospitality%20website%20with%20rustic%20upscale%20design&width=600&height=440&seq=portfolio-tipsytee-001&orientation=landscape"
                    alt="Tipsy Tee Tavern - golf themed bar and restaurant website built by Technelle"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                    width="600"
                    height="440"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                  <div className="absolute top-4 left-4">
                    <span className="bg-white/90 text-[#1a1a1a] text-[11px] uppercase tracking-wider font-bold px-3 py-1 rounded-full">Food &amp; Beverage</span>
                  </div>
                  <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-10 h-10 bg-[#2196F3] rounded-full flex items-center justify-center">
                      <i className="ri-external-link-line text-white text-lg" aria-hidden="true"></i>
                    </div>
                  </div>
                </div>
              </a>
              <div className="p-7">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-[20px] font-bold text-[#1a1a1a] leading-tight">Tipsy Tee Tavern</h3>
                    <a href="https://tipsyteetavern.com/" target="_blank" rel="noopener noreferrer" className="text-[13px] text-[#2196F3] hover:underline font-medium">tipsyteetavern.com</a>
                  </div>
                  <a href="https://tipsyteetavern.com/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-[#2196F3] hover:text-white transition-colors text-[#1a1a1a]" aria-label="Visit Tipsy Tee Tavern website">
                    <i className="ri-arrow-right-up-line text-base" aria-hidden="true"></i>
                  </a>
                </div>
                <p className="text-[14px] text-gray-600 leading-relaxed mb-5">
                  A golf-themed tavern and restaurant site built to drive reservations and event bookings. Features a full menu, private event inquiries, and a lively brand identity that captures the venue's personality.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="text-[12px] bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium">Restaurant</span>
                  <span className="text-[12px] bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium">Hospitality</span>
                  <span className="text-[12px] bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium">Bookings</span>
                </div>
              </div>
            </div>

            {/* Tony's Heating and Air */}
            <div className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-[#2196F3]/30 transition-all hover:-translate-y-1">
              <a href="https://www.tonysheatingandair.com" target="_blank" rel="noopener noreferrer" className="block">
                <div className="relative h-[220px] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=HVAC%20heating%20and%20cooling%20company%20website%20mockup%20showing%20a%20veteran%20owned%20service%20business%20in%20Milwaukee%20with%20bold%20orange%20and%20dark%20gray%20color%20scheme%20featuring%20service%20cards%20emergency%20repair%20scheduling%20and%20customer%20testimonials%20on%20a%20professional%20trade%20services%20website&width=600&height=440&seq=portfolio-tonys-001&orientation=landscape"
                    alt="Tony's Heating and Air - Milwaukee HVAC company website built by Technelle"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                    width="600"
                    height="440"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                  <div className="absolute top-4 left-4">
                    <span className="bg-white/90 text-[#1a1a1a] text-[11px] uppercase tracking-wider font-bold px-3 py-1 rounded-full">Trade Services</span>
                  </div>
                  <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-10 h-10 bg-[#2196F3] rounded-full flex items-center justify-center">
                      <i className="ri-external-link-line text-white text-lg" aria-hidden="true"></i>
                    </div>
                  </div>
                </div>
              </a>
              <div className="p-7">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-[20px] font-bold text-[#1a1a1a] leading-tight">Tony's Heating &amp; Air</h3>
                    <a href="https://www.tonysheatingandair.com" target="_blank" rel="noopener noreferrer" className="text-[13px] text-[#2196F3] hover:underline font-medium">tonysheatingandair.com</a>
                  </div>
                  <a href="https://www.tonysheatingandair.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-[#2196F3] hover:text-white transition-colors text-[#1a1a1a]" aria-label="Visit Tony's Heating and Air website">
                    <i className="ri-arrow-right-up-line text-base" aria-hidden="true"></i>
                  </a>
                </div>
                <p className="text-[14px] text-gray-600 leading-relaxed mb-5">
                  A veteran-owned Milwaukee HVAC company site built to convert local search traffic into booked service calls. Features emergency service CTAs, service area pages, and trust-building elements.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="text-[12px] bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium">HVAC</span>
                  <span className="text-[12px] bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium">Local SEO</span>
                  <span className="text-[12px] bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium">Milwaukee</span>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/case-studies"
              className="inline-flex items-center gap-2 bg-[#2196F3] text-white px-8 py-4 rounded-md text-[15px] font-medium hover:bg-[#1976D2] transition-colors"
            >
              <span>See All Case Studies</span>
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Lead Magnet */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Ebook Cover */}
            <div>
              <div className="w-full max-w-sm mx-auto">
                <div
                  className="relative rounded-2xl overflow-hidden flex flex-col justify-between"
                  style={{ aspectRatio: '3/4', background: 'linear-gradient(160deg, #0d1b2a 0%, #112240 60%, #0d1b2a 100%)' }}
                >
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-[#2196F3]"></div>
                  <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'linear-gradient(#2196F3 1px, transparent 1px), linear-gradient(90deg, #2196F3 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
                  <div className="relative z-10 p-8 flex flex-col h-full">
                    <div className="flex items-center gap-2 mb-8">
                      <div className="w-6 h-6 bg-[#2196F3] rounded-sm flex items-center justify-center">
                        <i className="ri-flashlight-line text-white text-xs" aria-hidden="true"></i>
                      </div>
                      <span className="text-[11px] uppercase tracking-widest text-[#2196F3] font-bold">Technelle</span>
                    </div>
                    <div className="inline-flex items-center gap-1.5 bg-[#2196F3]/20 border border-[#2196F3]/40 rounded-full px-3 py-1 mb-6 self-start">
                      <i className="ri-download-line text-[#2196F3] text-xs" aria-hidden="true"></i>
                      <span className="text-[10px] uppercase tracking-widest text-[#2196F3] font-bold">Free Guide</span>
                    </div>
                    <h3 className="text-[24px] font-bold text-white leading-tight mb-3">
                      10 AI Marketing Strategies That Drive Real ROI
                    </h3>
                    <p className="text-[13px] text-white/60 leading-relaxed mb-auto">
                      For Wisconsin Small &amp; Mid-Sized Businesses
                    </p>
                    <div className="w-12 h-0.5 bg-[#2196F3] my-6"></div>
                    <div className="border-t border-white/10 pt-5">
                      <div className="text-[10px] text-white/35 uppercase tracking-wider mb-2">Author</div>
                      <div className="text-[15px] font-semibold text-white">Melissa Thomas</div>
                      <div className="text-[12px] text-[#2196F3] mt-0.5">Technelle &nbsp;·&nbsp; Waukesha, Wisconsin</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Side */}
            <div>
              <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-4">Free Download</div>
              <h2 className="text-[36px] font-serif font-bold text-white mb-6 leading-tight">
                Free Guide: 10 AI Marketing Strategies for Wisconsin Businesses
              </h2>
              <p className="text-[17px] text-white/90 leading-relaxed mb-6">
                Download the practical guide Wisconsin SMBs are using to cut marketing waste and grow revenue with AI — no technical background required.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                  <span className="text-[15px] text-white/90">Why AI is your single biggest competitive advantage right now</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                  <span className="text-[15px] text-white/90">The 10 strategies generating measurable ROI for local businesses</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                  <span className="text-[15px] text-white/90">How to outrank competitors without doubling your ad budget</span>
                </li>
              </ul>
              <form className="space-y-4" onSubmit={handleNewsletterSubmit} data-readdy-form id="lead-magnet-form">
                <input type="text" name="name" placeholder="Your Name" required className="w-full border border-gray-600 bg-white/10 rounded-md px-4 py-3 text-[15px] text-white placeholder-gray-400 focus:outline-none focus:border-[#2196F3] transition-colors" />
                <input type="email" name="email" placeholder="Work Email" required className="w-full border border-gray-600 bg-white/10 rounded-md px-4 py-3 text-[15px] text-white placeholder-gray-400 focus:outline-none focus:border-[#2196F3] transition-colors" />
                <a
                  href="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/f101d0b5-ac2f-4206-95fc-3c290c8bf204_technelle_ai_marketing_guide.pdf?v=5a533806b2a50757bc66a9847c1790bf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-[#2196F3] text-white py-4 rounded-md text-[15px] font-medium hover:bg-[#1976D2] transition-colors cursor-pointer min-h-[44px] flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-download-line text-lg" aria-hidden="true"></i>
                  Download the Free Guide
                </a>
                <p className="text-[13px] text-gray-400 text-center">Instant download. No spam. Unsubscribe anytime.</p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted Partners */}
      <section className="bg-white py-20">
        <div className="container mx-auto px-4 sm:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-4">
              <h3 className="text-[28px] font-serif font-bold text-[#1a1a1a]">20</h3>
              <div>
                <span className="text-[20px] font-serif text-[#1a1a1a]">Years Serving Wisconsin</span>
                <div className="w-24 h-1 bg-[#2196F3] mt-1"></div>
              </div>
            </div>
            <div className="flex items-center gap-8 flex-wrap justify-center text-[15px] sm:text-[18px] font-medium text-[#1a1a1a]">
              <span>Wisconsin Hospitality</span>
              <span className="text-gray-300" aria-hidden="true">•</span>
              <span>Food &amp; Beverage</span>
              <span className="text-gray-300" aria-hidden="true">•</span>
              <span>Wisconsin Industrial</span>
              <span className="text-gray-300" aria-hidden="true">•</span>
              <span>Manufacturing</span>
              <span className="text-gray-300" aria-hidden="true">•</span>
              <span>Renewable Energy</span>
              <span className="text-gray-300" aria-hidden="true">•</span>
              <span>Trade Services</span>
              <span className="text-gray-300" aria-hidden="true">•</span>
              <span>Health &amp; Beauty</span>
              <span className="text-gray-300" aria-hidden="true">•</span>
              <span>Ecommerce</span>
            </div>
          </div>
        </div>
      </section>

      {/* Complete Growth System Section */}
      <section className="bg-gray-50 py-24" id="services">
        <div className="container mx-auto px-4 sm:px-8">
          <div className="text-center mb-16">
            <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-4">The complete growth system</div>
            <h2 className="text-[48px] sm:text-[54px] font-bold text-[#1a1a1a] mb-5 leading-tight">
              One operator. No agency markup.<br />Full-stack results.
            </h2>
            <p className="text-[18px] text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Most Wisconsin businesses hire 3–4 vendors to get what Technelle delivers as one integrated system — SEO, paid social, content, and AI automation working together toward one goal: measurable revenue growth.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-6">
            <div className="bg-white rounded-2xl p-8 hover:border-[#2196F3]/30 border border-transparent transition-all group">
              <div className="w-14 h-14 bg-[#1a1a1a] rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#2196F3] transition-colors">
                <i className="ri-search-eye-line text-white text-2xl" aria-hidden="true"></i>
              </div>
              <h3 className="text-[22px] font-bold text-[#1a1a1a] mb-3">SEO & AI Search Visibility</h3>
              <p className="text-[15px] text-gray-600 leading-relaxed mb-6">
                Get found on Google — including the new AI-generated answers that now drive most local search decisions.
              </p>
              <span className="inline-block bg-[#2196F3]/10 text-[#2196F3] text-[12px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full">
                Long-term organic growth
              </span>
            </div>

            <div className="bg-white rounded-2xl p-8 hover:border-[#2196F3]/30 border border-transparent transition-all group">
              <div className="w-14 h-14 bg-[#1a1a1a] rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#2196F3] transition-colors">
                <i className="ri-meta-line text-white text-2xl" aria-hidden="true"></i>
              </div>
              <h3 className="text-[22px] font-bold text-[#1a1a1a] mb-3">Meta Ads & Paid Social</h3>
              <p className="text-[15px] text-gray-600 leading-relaxed mb-6">
                Conversion-focused campaigns that turn ad spend into booked jobs — not just likes and impressions.
              </p>
              <span className="inline-block bg-[#2196F3]/10 text-[#2196F3] text-[12px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full">
                Immediate lead flow
              </span>
            </div>

            <div className="bg-white rounded-2xl p-8 hover:border-[#2196F3]/30 border border-transparent transition-all group">
              <div className="w-14 h-14 bg-[#1a1a1a] rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#2196F3] transition-colors">
                <i className="ri-article-line text-white text-2xl" aria-hidden="true"></i>
              </div>
              <h3 className="text-[22px] font-bold text-[#1a1a1a] mb-3">Content Strategy</h3>
              <p className="text-[15px] text-gray-600 leading-relaxed mb-6">
                Authority-building content that ranks, converts, and keeps your brand top of mind in your local market.
              </p>
              <span className="inline-block bg-[#2196F3]/10 text-[#2196F3] text-[12px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full">
                Trust & authority
              </span>
            </div>

            <div className="bg-white rounded-2xl p-8 hover:border-[#2196F3]/30 border border-transparent transition-all group sm:col-span-1">
              <div className="w-14 h-14 bg-[#1a1a1a] rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#2196F3] transition-colors">
                <i className="ri-robot-line text-white text-2xl" aria-hidden="true"></i>
              </div>
              <h3 className="text-[22px] font-bold text-[#1a1a1a] mb-3">AI Consulting & Automation</h3>
              <p className="text-[15px] text-gray-600 leading-relaxed mb-6">
                Identify where your business is leaking $20K–$80K annually and replace it with AI systems that run without staff.
              </p>
              <span className="inline-block bg-[#2196F3]/10 text-[#2196F3] text-[12px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full">
                Cost savings & scale
              </span>
            </div>

            <div className="bg-white rounded-2xl p-8 hover:border-[#2196F3]/30 border border-transparent transition-all group sm:col-span-1">
              <div className="w-14 h-14 bg-[#1a1a1a] rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#2196F3] transition-colors">
                <i className="ri-mail-send-line text-white text-2xl" aria-hidden="true"></i>
              </div>
              <h3 className="text-[22px] font-bold text-[#1a1a1a] mb-3">Email Marketing & Lead Nurturing</h3>
              <p className="text-[15px] text-gray-600 leading-relaxed mb-6">
                Automated campaigns that convert cold leads into paying customers — and keep existing customers coming back on autopilot.
              </p>
              <span className="inline-block bg-[#2196F3]/10 text-[#2196F3] text-[12px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full">
                Revenue on autopilot
              </span>
            </div>

            <div className="bg-[#1a1a1a] rounded-2xl p-8 border border-transparent transition-all group col-span-1 sm:col-span-2 lg:col-span-2">
              <div className="w-14 h-14 bg-[#2196F3] rounded-xl flex items-center justify-center mb-6">
                <i className="ri-user-star-line text-white text-2xl" aria-hidden="true"></i>
              </div>
              <h3 className="text-[22px] font-bold text-white mb-3">Fractional CMO</h3>
              <p className="text-[15px] text-white/75 leading-relaxed mb-6">
                Senior marketing leadership without the $150K salary. Strategy, execution, and accountability — all in one.
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <span className="inline-block bg-[#2196F3]/20 text-[#2196F3] text-[12px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full">
                  Leadership on demand
                </span>
                <Link
                  to="/services"
                  className="text-[14px] text-white/60 hover:text-white transition-colors flex items-center gap-1 whitespace-nowrap"
                >
                  See all service tiers <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
              </div>
            </div>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="border-t border-gray-200 my-12"></div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <div className="text-[56px] font-bold text-[#2196F3] leading-none mb-2">206%</div>
                <div className="text-[15px] text-gray-600 font-medium">Traffic increase — Tipsy Tee Tavern</div>
              </div>
              <div className="text-center">
                <div className="text-[56px] font-bold text-[#2196F3] leading-none mb-2">100%+</div>
                <div className="text-[15px] text-gray-600 font-medium">Food sales growth after SEO</div>
              </div>
              <div className="text-center">
                <div className="text-[56px] font-bold text-[#2196F3] leading-none mb-2">300+</div>
                <div className="text-[15px] text-gray-600 font-medium">Waitlist built with zero ad spend</div>
              </div>
            </div>

            <div className="bg-[#1a1a1a] rounded-2xl px-6 sm:px-10 py-10 flex flex-col lg:flex-row items-center justify-between gap-8">
              <div>
                <h3 className="text-[26px] font-bold text-white mb-2">Not sure where to start?</h3>
                <p className="text-[16px] text-white/65">Free 30-minute audit — I'll show you exactly where your business is losing money.</p>
              </div>
              <button
                onClick={() => setMenuOpen(true)}
                className="bg-[#2196F3] text-white px-10 py-4 rounded-md text-[16px] font-semibold hover:bg-[#1976D2] transition-colors whitespace-nowrap cursor-pointer flex-shrink-0"
              >
                Book Your Free Audit
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Preview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Proven Results for Wisconsin Businesses</h2>
            <p className="text-xl text-gray-600">See how we've helped real Wisconsin companies achieve measurable growth</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-rocket-line text-3xl text-blue-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Teen Work Hub</h3>
              <p className="text-gray-600 mb-6">Built brand, website, and full go-to-market strategy from zero. Achieved 300+ teen waitlist nationally and 10+ active job posters with zero paid marketing budget.</p>
              <div className="bg-white rounded-lg p-4 mb-6">
                <div className="text-3xl font-bold text-blue-500 mb-1">300+</div>
                <div className="text-sm text-gray-600">National Waitlist</div>
              </div>
              <p className="text-sm text-gray-500 italic">"Institutional interest from CESA 1 and Saskatchewan school districts — all organic."</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-fire-line text-3xl text-blue-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Tony's Heating &amp; Cooling</h3>
              <p className="text-gray-600 mb-6">Launched brand new website and implemented foundational SEO strategy for an HVAC business starting from zero online presence. Building organic search authority in Waukesha.</p>
              <div className="bg-white rounded-lg p-4 mb-6">
                <div className="text-3xl font-bold text-blue-500 mb-1">New</div>
                <div className="text-sm text-gray-600">Online Presence</div>
              </div>
              <p className="text-sm text-gray-500 italic">"If you're looking for a company that delivers top-notch work and values collaboration, you've found it."</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-restaurant-line text-3xl text-blue-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Tipsy Tee Tavern</h3>
              <p className="text-gray-600 mb-6">Executed complete website overhaul and implemented targeted SEO strategy. Results: 206% increase in website traffic and 100%+ increase in both traffic and food sales.</p>
              <div className="bg-white rounded-lg p-4 mb-6">
                <div className="text-3xl font-bold text-blue-500 mb-1">206%</div>
                <div className="text-sm text-gray-600">Traffic Increase</div>
              </div>
              <p className="text-sm text-gray-500 italic">"Complete transformation of our online presence and bottom line."</p>
            </div>
          </div>
        </div>
      </section>

      {/* Thought Leadership */}
      <section className="bg-gray-50 py-20">
        <div className="container mx-auto px-4 sm:px-8">
          <div className="text-center mb-12">
            <h2 className="text-[42px] font-serif font-bold text-[#1a1a1a] mb-4">
              Latest <span className="relative inline-block text-[#2196F3]">
                Insights
                <svg className="absolute -bottom-1 left-0 w-full" viewBox="0 0 150 6" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M2 4C60 2 110 2 148 4" stroke="#2196F3" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </span>
            </h2>
            <p className="text-[18px] text-gray-600 max-w-2xl mx-auto">Expert perspectives on AI marketing trends and strategies for Wisconsin businesses</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
            <Link to="/resources" className="bg-white rounded-xl overflow-hidden hover:shadow-lg transition-all group">
              <div className="h-48 bg-gradient-to-br from-[#2196F3] to-[#1565C0] flex items-center justify-center">
                <i className="ri-lightbulb-line text-white text-5xl" aria-hidden="true"></i>
              </div>
              <div className="p-6">
                <div className="text-[13px] uppercase tracking-wider text-[#2196F3] font-semibold mb-2">AI Strategy</div>
                <h3 className="text-[20px] font-bold text-[#1a1a1a] mb-3 group-hover:text-[#2196F3] transition-colors">10 AI Marketing Strategies That Drive 3x ROI</h3>
                <p className="text-[14px] text-gray-600">Learn how leading brands are using AI to transform their marketing performance...</p>
              </div>
            </Link>
            <Link to="/resources" className="bg-white rounded-xl overflow-hidden hover:shadow-lg transition-all group">
              <div className="h-48 bg-gradient-to-br from-[#2196F3] to-[#1565C0] flex items-center justify-center">
                <i className="ri-robot-line text-white text-5xl" aria-hidden="true"></i>
              </div>
              <div className="p-6">
                <div className="text-[13px] uppercase tracking-wider text-[#2196F3] font-semibold mb-2">Implementation</div>
                <h3 className="text-[20px] font-bold text-[#1a1a1a] mb-3 group-hover:text-[#2196F3] transition-colors">The Complete Guide to AI Marketing Automation</h3>
                <p className="text-[14px] text-gray-600">Step-by-step framework for implementing AI-powered marketing workflows...</p>
              </div>
            </Link>
            <Link to="/resources" className="bg-white rounded-xl overflow-hidden hover:shadow-lg transition-all group">
              <div className="h-48 bg-gradient-to-br from-[#2196F3] to-[#1565C0] flex items-center justify-center">
                <i className="ri-line-chart-line text-white text-5xl" aria-hidden="true"></i>
              </div>
              <div className="p-6">
                <div className="text-[13px] uppercase tracking-wider text-[#2196F3] font-semibold mb-2">Case Study</div>
                <h3 className="text-[20px] font-bold text-[#1a1a1a] mb-3 group-hover:text-[#2196F3] transition-colors">How We Increased Conversions by 127% Using AI</h3>
                <p className="text-[14px] text-gray-600">Real-world case study showing the power of AI-driven optimization...</p>
              </div>
            </Link>
          </div>
          <div className="text-center">
            <Link to="/resources" className="inline-flex items-center gap-2 bg-[#2196F3] text-white px-8 py-4 rounded-md text-[15px] font-medium hover:bg-[#1976D2] transition-colors">
              <span>View All Resources</span>
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* About Melissa Blurb */}
      <section className="bg-white py-16 border-t border-gray-100">
        <div className="container mx-auto px-4 sm:px-8">
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center sm:items-start gap-8">
            <div className="flex-shrink-0">
              <div className="relative w-20 h-20 rounded-full overflow-hidden ring-2 ring-[#2196F3] ring-offset-2">
                <img
                  src="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/055ed85d-fdf9-4a25-aaed-04055b9c4167_Headshot.png?v=34f4a642e8cdde208d06262d9ecec027"
                  alt="Melissa Thomas, founder of Technelle"
                  className="w-full h-full object-cover object-top"
                  width="80"
                  height="80"
                />
              </div>
            </div>
            <div>
              <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-2">A note from Melissa</div>
              <p className="text-[17px] text-[#1a1a1a] leading-relaxed mb-3 font-medium">
                "I started Technelle because I was tired of watching Wisconsin businesses get burned by agencies that overpromise, underdeliver, and disappear after the contract is signed."
              </p>
              <p className="text-[15px] text-gray-600 leading-relaxed mb-4">
                I'm a 20-year marketing operator — not a salesperson, not a project manager, not a middleman. When you hire Technelle, you get me: hands-on strategy, real execution, and someone who actually cares whether your business grows. I've worked across HVAC, restaurants, manufacturing, e-commerce, and more. I know what works in Wisconsin markets because I've done it here, for real clients, for two decades.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-1.5 text-[#2196F3] text-[14px] font-semibold hover:underline"
              >
                More about my background <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Calculator Section */}
      <section className="bg-[#0A1628] py-24">
        <div className="container mx-auto px-4 sm:px-8">
          <div className="max-w-5xl mx-auto">

            {/* Header */}
            <div className="text-center mb-14">
              <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-4">ROI Estimator</div>
              <h2 className="text-[34px] sm:text-[46px] font-serif font-bold text-white mb-5 leading-tight">
                What Is AI Worth to Your Wisconsin Business?
              </h2>
              <p className="text-[17px] sm:text-[18px] text-white/70 max-w-3xl mx-auto leading-relaxed">
                Most businesses we work with find $30,000 to $80,000 in annual savings in the first conversation. Here is a simple way to estimate yours.
              </p>
            </div>

            {/* Three Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">

              {/* Card 1 — Employee Time */}
              <div className="bg-[#0d1f35] border border-white/10 rounded-2xl p-8 flex flex-col hover:border-[#2196F3]/50 transition-all">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-[#2196F3]/15 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-[#2196F3] text-lg" aria-hidden="true"></i>
                  </div>
                  <div className="text-[11px] uppercase tracking-widest text-[#2196F3] font-bold">Employee Time</div>
                </div>
                <div className="text-[38px] font-bold text-white leading-none mb-1">$33,000</div>
                <div className="text-[13px] text-[#2196F3] font-semibold mb-5">annual labor cost recoverable</div>
                <p className="text-[14px] text-white/65 leading-relaxed flex-1">
                  If you have 5 employees each spending 5 hours per week on repetitive tasks — at an average salary of $50,000 per year — that is $33,000 in annual labor cost that AI can largely eliminate. Most businesses have more than 5 people in this situation.
                </p>
              </div>

              {/* Card 2 — Missed Leads */}
              <div className="bg-[#0d1f35] border border-[#2196F3]/40 rounded-2xl p-8 flex flex-col relative">
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="bg-[#2196F3] text-white text-[10px] uppercase tracking-widest font-bold px-4 py-1.5 rounded-full whitespace-nowrap">Most Common</span>
                </div>
                <div className="flex items-center gap-3 mb-5 mt-2">
                  <div className="w-10 h-10 bg-[#2196F3]/15 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-alarm-warning-line text-[#2196F3] text-lg" aria-hidden="true"></i>
                  </div>
                  <div className="text-[11px] uppercase tracking-widest text-[#2196F3] font-bold">Missed Leads</div>
                </div>
                <div className="text-[38px] font-bold text-white leading-none mb-1">$60,000</div>
                <div className="text-[13px] text-[#2196F3] font-semibold mb-5">per year walking out the door</div>
                <p className="text-[14px] text-white/65 leading-relaxed flex-1">
                  If your business gets 50 inquiries per month and you miss 20% after hours — at an average job value of $500 — that is $5,000 in missed revenue every single month. $60,000 per year walking out the door.
                </p>
              </div>

              {/* Card 3 — Marketing Waste */}
              <div className="bg-[#0d1f35] border border-white/10 rounded-2xl p-8 flex flex-col hover:border-[#2196F3]/50 transition-all">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-[#2196F3]/15 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-funds-line text-[#2196F3] text-lg" aria-hidden="true"></i>
                  </div>
                  <div className="text-[11px] uppercase tracking-widest text-[#2196F3] font-bold">Marketing Waste</div>
                </div>
                <div className="text-[38px] font-bold text-white leading-none mb-1">&lt;30¢</div>
                <div className="text-[13px] text-[#2196F3] font-semibold mb-5">value per dollar spent</div>
                <p className="text-[14px] text-white/65 leading-relaxed flex-1">
                  If you are spending $2,000 per month on marketing with no clear tracking, no automated follow-up, and no SEO strategy — you are likely recovering less than 30 cents of value for every dollar spent. AI-powered marketing changes that math immediately.
                </p>
              </div>

            </div>

            {/* Bold summary line */}
            <div className="border-t border-white/10 pt-10 text-center">
              <p className="text-[18px] sm:text-[20px] font-bold text-white leading-snug mb-8 max-w-3xl mx-auto">
                The average Wisconsin business we audit recovers{' '}
                <span className="text-[#2196F3]">$42,000 in annual value</span>{' '}
                within the first 90 days of working with Technelle.
              </p>
              <a
                href="https://calendly.com/melissa-technelle/30min?utm_source=technelle&utm_medium=website&utm_campaign=audit&utm_content=homepage"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#2196F3] text-white px-10 py-4 rounded-md text-[16px] font-semibold hover:bg-[#1976D2] transition-colors whitespace-nowrap cursor-pointer"
              >
                Find Out What Your Business Could Recover
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </a>
            </div>

          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="bg-white py-24">
        <div className="container mx-auto px-4 sm:px-8 text-center">
          <h2 className="text-[36px] sm:text-[48px] lg:text-[56px] font-serif font-bold text-[#1a1a1a] mb-6 leading-tight">
            See Exactly How to<br />
            <span className="relative inline-block text-[#2196F3]">
              Double Your Leads in 90 Days
              <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 400 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M2 6C100 2 300 2 398 6" stroke="#2196F3" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </span>
          </h2>
          <p className="text-[20px] text-gray-600 mb-10 max-w-2xl mx-auto">
            Get a custom roadmap showing the exact AI strategies and marketing tactics that will work for your Wisconsin business — completely free.
          </p>
          <button
            onClick={() => setMenuOpen(true)}
            className="bg-[#2196F3] text-white px-12 py-5 rounded-md text-[17px] font-semibold hover:bg-[#1976D2] transition-colors cursor-pointer min-w-[44px] min-h-[44px] shadow-xl"
          >
            <span>Get My Free Marketing Audit</span>
          </button>
          <p className="text-[14px] text-gray-500 mt-6">
            <i className="ri-shield-check-line mr-2" aria-hidden="true"></i>

          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="bg-gray-50 py-24">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-4">FAQs</div>
            <h2 className="text-[36px] sm:text-[48px] font-serif font-bold text-[#1a1a1a] mb-4">
              Frequently Asked <span className="relative inline-block text-[#2196F3]">
                Questions
                <svg className="absolute -bottom-1 left-0 w-full" viewBox="0 0 200 6" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M2 4C60 2 140 2 198 4" stroke="#2196F3" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </span>
            </h2>
          </div>
          <div className="max-w-4xl mx-auto space-y-4">
            {[
              { q: "How does Technelle approach creative and strategy development?", a: "We follow a structured multi-phase process tailored to Wisconsin businesses. First, we conduct discovery through interviews, workshops, and audience analysis. Next, we create a strategic framework that informs content, design, and channel decisions. Execution follows with SEO-driven content, campaign builds, and creative production optimized for the Wisconsin market." },
              { q: "How long does a typical project take?", a: "Timelines vary by deliverable. Standard design and content projects for Wisconsin businesses are completed within days or weeks, depending on project scope. Larger engagements, such as full brand development or multi-platform campaigns, follow multi-month timelines with defined milestones." },
              { q: "What kind of ROI can I expect?", a: "ROI varies by scope, but our engagements with Wisconsin businesses focus on measurable outcomes such as increased organic visibility, improved conversion rates, higher lead volume, and stronger audience retention. We define KPIs at the outset and track performance throughout." },
              { q: "What industries do you specialize in?", a: "We work across Wisconsin's key sectors including Hospitality, Food & Beverage, Industrial, Utilities, Manufacturing, and other innovation-forward industries. Our Wisconsin clients choose us for strategic storytelling, digital performance, and creative that adapts to their market." }
            ].map((faq, index) => (
              <details key={index} className="bg-white rounded-xl p-6 group">
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="text-[17px] font-semibold text-[#1a1a1a]">
                    <span className="text-gray-400 mr-3">{String(index + 1).padStart(2, '0')}.</span>
                    {faq.q}
                  </span>
                  <i className="ri-arrow-down-s-line text-2xl text-gray-400 group-open:rotate-180 transition-transform"></i>
                </summary>
                <p className="mt-4 text-[15px] text-gray-700 leading-relaxed pl-10">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a1a1a] text-white py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            <div className="lg:col-span-1">
              <div className="flex items-center gap-3 mb-6">
                <img src="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/d2c67bee-2653-4219-a221-2e9d779e273e_ChatGPT-Image-Apr-2-2026-02_21_57-PM.png?v=d5a360501a5e3b86884e2dc0f7061a82" alt="Technelle" className="h-60" width="240" height="240" />
              </div>
            </div>
            <div>
              <h5 className="text-[14px] uppercase tracking-widest text-[#2196F3] font-semibold mb-4">Links</h5>
              <nav className="space-y-3">
                <Link to="/" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Home</Link>
                <Link to="/about" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">About</Link>
                <Link to="/services" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Services</Link>
                <Link to="/industries" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Industries</Link>
                <Link to="/resources" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Resources</Link>
                <Link to="/how-we-work" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">How We Work</Link>
                <Link to="/case-studies" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Case Studies</Link>
                <Link to="/faq" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">FAQ</Link>
                <Link to="/service-areas" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Service Areas</Link>
                <Link to="/contact" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Contact</Link>
              </nav>
            </div>
            <div>
              <h5 className="text-[14px] uppercase tracking-wider text-gray-400 mb-6 font-semibold">Legal</h5>
              <nav className="space-y-3">
                <Link to="/privacy-policy" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Privacy Policy</Link>
                <Link to="/terms" className="block text-white hover:text-[#2196F3] transition-colors text-[15px] min-h-[44px] flex items-center">Terms &amp; Conditions</Link>
              </nav>
            </div>
            <div>
              <h5 className="text-[14px] uppercase tracking-wider text-gray-400 mb-6 font-semibold">Newsletter</h5>
              <h6 className="text-white mb-4 text-[15px]">Get monthly AI marketing insights</h6>
              <form className="relative" onSubmit={handleNewsletterSubmit} data-readdy-form id="newsletter-form">
                <label htmlFor="newsletter-email" className="sr-only">Email address for newsletter</label>
                <input
                  id="newsletter-email" type="email" name="email" placeholder="Enter your email" required
                  aria-label="Email address for newsletter subscription"
                  className="w-full bg-white/10 border border-white/20 rounded-md px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-[#2196F3] transition-colors text-[14px] min-h-[44px]"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#2196F3] text-white px-4 py-2 rounded text-[13px] font-medium hover:bg-[#1976D2] transition-colors cursor-pointer min-w-[44px] min-h-[36px]"
                  aria-label="Subscribe to newsletter"
                >
                  Sign Up
                </button>
              </form>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-6">
                <button onClick={() => setMenuOpen(true)} className="text-white hover:text-[#2196F3] transition-colors text-[14px] cursor-pointer min-w-[44px] min-h-[44px]" aria-label="Contact us">Contact Us</button>

              </div>
              <div className="text-[14px] text-gray-400">Technelle™ • Waukesha, Wisconsin</div>
              <div className="flex items-center gap-6">
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#2196F3] transition-colors cursor-pointer min-w-[44px] min-h-[44px] flex items-center">LinkedIn</a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#2196F3] transition-colors cursor-pointer min-w-[44px] min-h-[44px] flex items-center">Instagram</a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#2196F3] transition-colors cursor-pointer min-w-[44px] min-h-[44px] flex items-center">Twitter</a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Scroll to Top */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 w-12 h-12 bg-[#2196F3] text-white rounded-full flex items-center justify-center hover:bg-[#1976D2] transition-all shadow-lg z-40 cursor-pointer min-w-[44px] min-h-[44px]"
        aria-label="Scroll to top of page"
      >
        <i className="ri-arrow-up-line text-xl" aria-hidden="true"></i>
      </button>
    </div>
  );
}
